import { Button } from '@dnb/eufemia';
import classnames from 'classnames';
import { Children, PropsWithChildren, useState } from 'react';
import { createPortal } from 'react-dom';

import style from './index.module.css';

interface Props {
  noPadding?: boolean;
}

/**
 * Mobile menu component that displays a burger button and overlay menu for smaller screens.
 *
 * @param children the content to display in the mobile menu.
 * @param noPadding option to remove the default padding provided by the component.
 */

export default function HeroMobileMenu({ children, noPadding = false }: PropsWithChildren<Props>) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Filter out falsy children (null, undefined, false)
  const validChildren = Children.toArray(children).filter(Boolean);
  const hasChildren = validChildren.length > 0;

  if (!hasChildren) {
    return null;
  }

  const handleMobileMenuClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();

    // Close menu if an anchor tag was clicked
    const target = e.target as HTMLElement;
    if (target.tagName === 'A' || target.closest('a')) {
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <div className={style['BurgerContainer']}>
      <button
        aria-expanded={isMobileMenuOpen}
        aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
        className={classnames(style['BurgerButton'])}
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
      >
        <span className={style['BurgerIcon']} />
        <span className={style['BurgerIcon']} />
        <span className={style['BurgerIcon']} />
      </button>

      {/* Mobile menu overlay */}
      {isMobileMenuOpen &&
        createPortal(
          <div className={style['MobileMenuOverlay']} onClick={() => setIsMobileMenuOpen(false)}>
            <div
              className={classnames(style['MobileMenu'], {
                [style['Padding']!]: !noPadding,
              })}
              onClick={handleMobileMenuClick}
            >
              <div className={style['CloseButton']}>
                <Button
                  icon="close"
                  icon_position="left"
                  onClick={() => setIsMobileMenuOpen(false)}
                  right="small"
                  size="large"
                  text="Close"
                  variant="tertiary"
                />
              </div>

              {children}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
